import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { participantsApi, type Device, type TaskTemplate } from "../api/participants";
import { qk } from "../../../../lib/queryKeys";

export const ParticipantSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const devicesQuery = useQuery({
    queryKey: qk.devices(),
    queryFn: () => participantsApi.listDevices(),
  });
  const templatesQuery = useQuery({
    queryKey: qk.taskTemplates(),
    queryFn: () => participantsApi.listTaskTemplates(),
  });

  const [devices, setDevices] = useState<Device[]>([]);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [newDevice, setNewDevice] = useState({ name: "", type: "" });
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    category: "",
  });

  useEffect(() => {
    if (devicesQuery.data?.data) setDevices(devicesQuery.data.data);
  }, [devicesQuery.data]);

  useEffect(() => {
    if (templatesQuery.data?.data) setTemplates(templatesQuery.data.data);
  }, [templatesQuery.data]);

  const createDeviceMutation = useMutation({
    mutationFn: (payload: Partial<Device>) => participantsApi.createDevice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.devices() });
      setNewDevice({ name: "", type: "" });
    },
  });

  const updateDeviceMutation = useMutation({
    mutationFn: ({ deviceId, payload }: { deviceId: string; payload: Partial<Device> }) =>
      participantsApi.updateDevice(deviceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.devices() });
    },
  });

  const deleteDeviceMutation = useMutation({
    mutationFn: (deviceId: string) => participantsApi.deleteDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.devices() });
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: (payload: Partial<TaskTemplate>) =>
      participantsApi.createTaskTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.taskTemplates() });
      setNewTemplate({ name: "", description: "", category: "" });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ templateId, payload }: { templateId: string; payload: Partial<TaskTemplate> }) =>
      participantsApi.updateTaskTemplate(templateId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.taskTemplates() });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: string) => participantsApi.deleteTaskTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.taskTemplates() });
    },
  });

  return (
    <Box>
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }} elevation={2}>
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={600}>
              Participant settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Maintain shared device options and default task templates.
            </Typography>
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              lg: 6
            }}>
            <Paper sx={{ p: 3 }} elevation={2}>
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography variant="h6">Devices</Typography>
                  <Typography variant="body2" color="text.secondary">
                    These appear in the participant device selector.
                  </Typography>
                </Stack>

                <Grid container spacing={2} alignItems="center">
                  <Grid
                    size={{
                      xs: 12,
                      md: 5
                    }}>
                    <TextField
                      label="Device name"
                      value={newDevice.name}
                      onChange={(event) =>
                        setNewDevice((prev) => ({ ...prev, name: event.target.value }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 5
                    }}>
                    <TextField
                      label="Type"
                      value={newDevice.type}
                      onChange={(event) =>
                        setNewDevice((prev) => ({ ...prev, type: event.target.value }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 2
                    }}>
                    <Button
                      variant="contained"
                      onClick={() =>
                        createDeviceMutation.mutate({
                          name: newDevice.name,
                          type: newDevice.type || null,
                          active: true,
                        })
                      }
                      disabled={!newDevice.name || createDeviceMutation.isPending}
                      fullWidth
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>

                <Divider />

                {devicesQuery.isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading devices...</Typography>
                  </Box>
                ) : devicesQuery.isError ? (
                  <Alert severity="error">Failed to load devices.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Active</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {devices.map((device) => (
                          <TableRow key={device.id}>
                            <TableCell sx={{ minWidth: 160 }}>
                              <TextField
                                value={device.name}
                                onChange={(event) =>
                                  setDevices((prev) =>
                                    prev.map((item) =>
                                      item.id === device.id
                                        ? { ...item, name: event.target.value }
                                        : item
                                    )
                                  )
                                }
                                variant="standard"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                              <TextField
                                value={device.type || ""}
                                onChange={(event) =>
                                  setDevices((prev) =>
                                    prev.map((item) =>
                                      item.id === device.id
                                        ? { ...item, type: event.target.value }
                                        : item
                                    )
                                  )
                                }
                                variant="standard"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={Boolean(device.active)}
                                    onChange={(event) =>
                                      setDevices((prev) =>
                                        prev.map((item) =>
                                          item.id === device.id
                                            ? { ...item, active: event.target.checked }
                                            : item
                                        )
                                      )
                                    }
                                  />
                                }
                                label=""
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    updateDeviceMutation.mutate({
                                      deviceId: device.id,
                                      payload: device,
                                    })
                                  }
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="text"
                                  color="error"
                                  size="small"
                                  onClick={() => deleteDeviceMutation.mutate(device.id)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid
            size={{
              xs: 12,
              lg: 6
            }}>
            <Paper sx={{ p: 3 }} elevation={2}>
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography variant="h6">Task templates</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Defaults applied when participants are created.
                  </Typography>
                </Stack>

                <Grid container spacing={2} alignItems="center">
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <TextField
                      label="Template name"
                      value={newTemplate.name}
                      onChange={(event) =>
                        setNewTemplate((prev) => ({ ...prev, name: event.target.value }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 5
                    }}>
                    <TextField
                      label="Description"
                      value={newTemplate.description}
                      onChange={(event) =>
                        setNewTemplate((prev) => ({
                          ...prev,
                          description: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 2
                    }}>
                    <TextField
                      label="Category"
                      value={newTemplate.category}
                      onChange={(event) =>
                        setNewTemplate((prev) => ({
                          ...prev,
                          category: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 1
                    }}>
                    <Button
                      variant="contained"
                      onClick={() =>
                        createTemplateMutation.mutate({
                          name: newTemplate.name,
                          description: newTemplate.description || null,
                          category: newTemplate.category || null,
                          active: true,
                          sort_order: 0,
                          applies_to: "participant",
                        })
                      }
                      disabled={!newTemplate.name || createTemplateMutation.isPending}
                      fullWidth
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>

                <Divider />

                {templatesQuery.isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading templates...</Typography>
                  </Box>
                ) : templatesQuery.isError ? (
                  <Alert severity="error">Failed to load templates.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Active</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {templates.map((template) => (
                          <TableRow key={template.id}>
                            <TableCell sx={{ minWidth: 160 }}>
                              <TextField
                                value={template.name}
                                onChange={(event) =>
                                  setTemplates((prev) =>
                                    prev.map((item) =>
                                      item.id === template.id
                                        ? { ...item, name: event.target.value }
                                        : item
                                    )
                                  )
                                }
                                variant="standard"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              <TextField
                                value={template.description || ""}
                                onChange={(event) =>
                                  setTemplates((prev) =>
                                    prev.map((item) =>
                                      item.id === template.id
                                        ? { ...item, description: event.target.value }
                                        : item
                                    )
                                  )
                                }
                                variant="standard"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                              <TextField
                                value={template.category || ""}
                                onChange={(event) =>
                                  setTemplates((prev) =>
                                    prev.map((item) =>
                                      item.id === template.id
                                        ? { ...item, category: event.target.value }
                                        : item
                                    )
                                  )
                                }
                                variant="standard"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={Boolean(template.active)}
                                    onChange={(event) =>
                                      setTemplates((prev) =>
                                        prev.map((item) =>
                                          item.id === template.id
                                            ? { ...item, active: event.target.checked }
                                            : item
                                        )
                                      )
                                    }
                                  />
                                }
                                label=""
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    updateTemplateMutation.mutate({
                                      templateId: template.id,
                                      payload: template,
                                    })
                                  }
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="text"
                                  color="error"
                                  size="small"
                                  onClick={() => deleteTemplateMutation.mutate(template.id)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
